const SUPABASE_STORAGE_HOST = "bpmvddwqkmccwhvlqvqz.supabase.co";

/**
 * Optimise a Supabase Storage image URL by appending render-transform params.
 * Supabase automatically negotiates WebP/AVIF via the Accept header when the
 * request goes through /storage/v1/render/image/…, so we don't have to force
 * a format ourselves — modern browsers get WebP, legacy browsers stay on the
 * original format.
 * Falls through unchanged for non-Supabase URLs.
 */
export function optimizeImageUrl(
  url: string,
  opts: { width?: number; height?: number; quality?: number } = {}
): string {
  if (!url || !url.includes(SUPABASE_STORAGE_HOST)) return url;

  const transformed = url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/"
  );

  const params = new URLSearchParams();
  if (opts.width) params.set("width", String(opts.width));
  if (opts.height) params.set("height", String(opts.height));
  // 72 is visually indistinguishable from 90 for product photography
  // but roughly halves the transferred bytes.
  params.set("quality", String(opts.quality ?? 72));
  params.set("resize", "contain");

  const separator = transformed.includes("?") ? "&" : "?";
  return `${transformed}${separator}${params.toString()}`;
}

/**
 * Generate a srcSet string for responsive images from a Supabase storage URL.
 */
export function responsiveSrcSet(
  url: string,
  widths: number[],
  quality = 72
): string {
  if (!url || !url.includes(SUPABASE_STORAGE_HOST)) return "";
  return widths
    .map((w) => `${optimizeImageUrl(url, { width: w, quality })} ${w}w`)
    .join(", ");
}

/**
 * Standard responsive widths for product-card thumbnails (grid cells).
 * Kept intentionally small (2 variants): every distinct width is a separate
 * on-demand transform on Supabase's render endpoint, and *cold* transforms can
 * take seconds. Fewer variants = far fewer cold misses for first-time visitors,
 * while 300/600 still covers 1x and 2x DPR at card size with no quality loss.
 */
export const CARD_WIDTHS = [300, 600];

/** Standard responsive widths for the main product-detail image. */
export const DETAIL_WIDTHS = [400, 600, 900, 1200];
/** Tiny thumbs (gallery strip, search dropdown). */
export const THUMB_WIDTHS = [96, 160];
